import React, { useContext } from "react";
import moment from "moment";
import { Modal, Select, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates"; // adjust import if your repo uses a different date picker
import {
  HiOfficeBuilding,
  HiViewGrid,
  HiLocationMarker,
  HiUserGroup,
} from "react-icons/hi";
import { EButton, EPageTitle, ETable } from "../../libs/ui";
import PageLayout from "../../PageLayout";
import useTeacherTrainingAttendance from "./hook/useTeacherTrainingAttendance";
import AttendanceHeatMapCard from "./components/AttendanceHeatMapCard";
import DistrictAttendanceTable from "./components/DistrictAttendanceTable";
import AttendanceStatCard from "./components/AttendanceStatCard";
import "./TeacherTrainingAttendanceDashboard.scss";
import { AppContext } from "../../AppContext";
import { ROLES } from "../../Routes/routes";

export default function TeacherTrainingAttendanceDashboard() {
  const { user, role } = useContext(AppContext);
  const {
    trainingTitle,
    setTrainingTitle,
    selectedDate,
    setSelectedDate,
    selectedDistrict,
    setSelectedDistrict,
    selectedBlock,
    setSelectedBlock,
    districts,
    blocks,
    handleApplyFilter,
    handleClearFilter,
    handleRefresh,
    lastUpdated,

    isLoadingOverallStats,
    overallStats,

    isLoadingMorning,
    morningData,
    isLoadingEvening,
    eveningData,

    downloadMap,
    downloadReport,
    isDownloadingReport,
    setSelectedSession,
    selectedSession,
    sessions,
    trainingTitleOptions,
    dateRange,
    overallMap,
    morningMapData,
    eveningMapData,
    appliedDistrict,
    isLoadingOverallMap,
    isReportModalOpen,
    setIsReportModalOpen,
    isLoadingReportRows,
    reportRows,
    handleViewReport,
  } = useTeacherTrainingAttendance();

  const reportLevel = {
    levelNames: appliedDistrict ? "district" : "state",
    districtName:
      districts && appliedDistrict
        ? districts.filter((e: any) => e?.id == +appliedDistrict)[0]?.name
        : "Haryana",
    districtId: appliedDistrict ? +appliedDistrict : 0,
    blockName:
      blocks && selectedBlock
        ? blocks.filter((e: any) => e?.id == +selectedBlock)[0]?.name
        : "",
  };
  return (
    <PageLayout>
      {/* Header */}
      <div className="flex justify-between items-center">
        <EPageTitle
          title={"Teacher Training Attendance"}
          description="Monitoring teacher training attendance for Morning &amp; Evening
            sessions"
        />
        {/* <div className="mt-2">
          <h3 className="text-lg font-semibold">Teacher Training</h3>
          <p className="text-sm text-gray-500">
            Monitoring teacher training attendance for Morning &amp; Evening
            sessions
          </p>
        </div> */}
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>
            Last Updated: {moment(lastUpdated).format("hh:mm A | DD MMM YYYY")}
          </span>
          <EButton variant="subtle" onClick={handleRefresh}>
            Refresh
          </EButton>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-container mt-4">
        <div className="flex justify-between gap-3 items-end">
          <Select
            label="Select Session"
            labelProps={{ className: "font-semibold" }}
            placeholder="Select Session"
            data={[
              ...sessions
                ?.filter((e) => e?.session_Id === 4)
                ?.map((e) => {
                  return {
                    value: String(e?.session_Id),
                    label: e?.session_Name,
                  };
                }),
            ]}
            value={selectedSession}
            onChange={(e) => setSelectedSession(String(e))}
          />
          <Select
            label="Training Title"
            labelProps={{ className: "font-semibold" }}
            data={trainingTitleOptions?.map((e: any) => {
              return {
                value: String(e?.training_Schedule_Header_Id),
                label: e?.schedule_Header_Title,
              };
            })}
            placeholder="Select Training Title"
            clearable
            value={trainingTitle}
            onChange={(e) => {
              setTrainingTitle(String(e));
              setSelectedDate(null); // Reset selected date when training title changes
            }}
          />
          <DatePicker
            label="Select Date"
            labelProps={{ className: "font-semibold" }}
            placeholder="Select date"
            minDate={dateRange?.min}
            maxDate={dateRange?.max}
            clearable={false}
            value={selectedDate}
            onChange={(e: any) => {
              setSelectedDate(e);
            }}
          />
          <Select
            label="District"
            labelProps={{ className: "font-semibold" }}
            placeholder="All Districts"
            clearable
            disabled={
              role?.includes(ROLES.BLOCK) || role?.includes(ROLES.DISTRICT)
                ? true
                : false
            }
            data={
              districts?.map((each: any) => ({
                label: each.name,
                value: String(each.id),
              })) ?? []
            }
            value={selectedDistrict ?? null}
            onChange={(e: any) => {
              setSelectedBlock(undefined);
              setSelectedDistrict(e);
            }}
          />

          <Select
            label="Block"
            labelProps={{ className: "font-semibold" }}
            placeholder="All Blocks"
            clearable
            disabled={
              !selectedDistrict || role?.includes(ROLES.BLOCK) ? true : false
            }
            data={
              blocks?.map((each: any) => ({
                label: each.name,
                value: String(each.id),
              })) ?? []
            }
            value={selectedBlock ?? null}
            onChange={(e: any) => setSelectedBlock(e)}
          />

          <div className="flex gap-3">
            <EButton className="btn-blue" onClick={handleApplyFilter}>
              Search
            </EButton>
            <EButton variant="outline" onClick={handleClearFilter}>
              Clear
            </EButton>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="flex gap-4 mt-6 flex-wrap">
        {/* <AttendanceStatCard
          label="Total Districts"
          value={overallStats?.TotalDistricts ?? "--"}
          icon={HiOfficeBuilding}
        />
        <AttendanceStatCard
          label="Total Blocks"
          value={overallStats?.TotalBlocks ?? "--"}
          icon={HiViewGrid}
        /> */}
        <AttendanceStatCard
          label="Total Training Batches"
          value={overallStats?.totalBatch ?? "--"}
          icon={HiLocationMarker}
          isLoading={isLoadingOverallStats}
        />
        <AttendanceStatCard
          label="Total Teachers"
          value={overallStats?.totalTeachers ?? "--"}
          icon={HiUserGroup}
          isLoading={isLoadingOverallStats}
        />
        <AttendanceStatCard
          label="Good (≥ 90%)"
          value={overallStats?.goodCount ?? "--"}
          // subLabel={
          //   overallStats ? `${overallStats.GoodPercentage}%` : undefined
          // }
          bgClass="bg-green-50"
          textClass="text-green-600"
          isLoading={isLoadingOverallStats}
        />
        <AttendanceStatCard
          label="Moderate (70% - 89%)"
          value={overallStats?.moderateCount ?? "--"}
          // subLabel={
          //   overallStats ? `${overallStats.ModeratePercentage}%` : undefined
          // }
          bgClass="bg-amber-50"
          textClass="text-amber-600"
          isLoading={isLoadingOverallStats}
        />
        <AttendanceStatCard
          label="Poor (< 70%)"
          value={overallStats?.poorCount ?? "--"}
          // subLabel={
          //   overallStats ? `${overallStats.PoorPercentage}%` : undefined
          // }
          bgClass="bg-red-50"
          textClass="text-red-600"
          isLoading={isLoadingOverallStats}
        />
        {/* <AttendanceStatCard
          label="No Data"
          value={overallStats?.NoDataCount ?? "--"}
          // subLabel={
          //   overallStats ? `${overallStats.NoDataPercentage}%` : undefined
          // }
          bgClass="bg-gray-50"
          textClass="text-gray-500"
        /> */}
      </div>

      {/* Heat maps */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <AttendanceHeatMapCard
          title="Morning Attendance"
          isLoading={isLoadingOverallMap}
          data={morningMapData}
          reportLevel={reportLevel}
          uniqueId="teacherTrainingMorning"
          onViewDistrictList={() => {}}
          onDownloadMap={() => downloadMap("morning")}
          type="morning"
        />
        <AttendanceHeatMapCard
          title="Evening Attendance"
          isLoading={isLoadingOverallMap}
          data={eveningMapData}
          reportLevel={reportLevel}
          uniqueId="teacherTrainingEvening"
          onViewDistrictList={() => {}}
          onDownloadMap={() => downloadMap("evening")}
          type="evening"
        />
      </div>

      {/* District-wise tables */}
      {/* <div className="grid grid-cols-2 gap-4 mt-6">
        <DistrictAttendanceTable
          title="District Wise Attendance - Morning (Cutoff: 10 AM)"
          isLoading={isLoadingMorning}
          rows={morningData?.Districts}
          onViewDistrict={(row) => {
            // TODO: wire to a drill-down (block/venue level), same idea as
            // your Advanced Search -> SchoolProfileDetail pattern
            console.log("View district", row);
          }}
        />
        <DistrictAttendanceTable
          title="District Wise Attendance - Evening (Cutoff: 3 PM)"
          isLoading={isLoadingEvening}
          rows={eveningData?.Districts}
          onViewDistrict={(row) => {
            console.log("View district", row);
          }}
        />
      </div> */}

      {/* Info banner */}
      {/* <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3 mt-6">
        Attendance to be marked as 'Present' or 'Absent' only. Toggle button
        should turn Green for Present and Red for Absent (real time).
      </div> */}

      {/* Footer actions */}
      <div className="flex justify-end gap-3 mt-4">
        <div className="flex justify-end gap-3 mt-2">
          {/* <EButton
            variant="outline"
            loading={isLoadingReportRows}
            onClick={handleViewReport}
          >
            View Report
          </EButton> */}
          <EButton
            className="btn-blue"
            loading={isDownloadingReport}
            onClick={downloadReport}
          >
            Download Report
          </EButton>
        </div>

        <Modal
          size="xxl"
          opened={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          title="Teacher Training Attendance Report"
        >
          <ETable>
            <thead>
              <tr>
                {reportRows[0] &&
                  Object.keys(reportRows[0]).map((key) => (
                    <th key={key}>{key.replaceAll("_", " ")}</th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {reportRows.map((row: any, i: number) => (
                <tr key={i}>
                  {Object.keys(reportRows[0]).map((key) => (
                    <td key={key}>{row[key] ?? "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </ETable>
        </Modal>
      </div>
    </PageLayout>
  );
}
